import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Sparkles, ArrowLeft } from "lucide-react";

const INDUSTRIES = ["Technology / SaaS","Financial Services","Healthcare","Manufacturing","Retail / E-commerce","Consulting / Services","Media / Entertainment","Energy","Non-profit","Other"];
const SIZES = ["1-50","51-200","201-1,000","1,001-5,000","5,001-25,000","25,000+"];

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company_name: "", company_website: "", industry: "Technology / SaaS", country: "United States", employee_count: "51-200",
    name: "", email: "", password: "",
  });
  const [loading, setLoading] = useState(false);

  const next = (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Enter a company name");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { company } = await signup(form);
      toast.success(`Welcome to ${company.name} on PeopleIQ`);
      nav(`/t/${company.slug}/onboarding`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d[0]?.msg : "Signup failed";
      toast.error(msg || "Signup failed");
    } finally { setLoading(false); }
  };

  const slug = form.company_name.trim().toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 40) || "workspace";

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-login">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </div>

      <div className="max-w-2xl mx-auto px-6 pb-16">
        <div className="mb-8 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold">P</div>
          <span className="font-display text-lg font-semibold tracking-tight">PeopleIQ</span>
        </div>

        <div className="chip chip-primary mb-4"><Sparkles size={12} /> Step {step} of 2</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">
          {step === 1 ? "Create your workspace" : "Add your admin account"}
        </h1>
        <p className="text-slate-500 mt-3 mb-8 max-w-lg">
          {step === 1
            ? "Your company gets a dedicated PeopleIQ workspace with isolated data, users, and branding."
            : "You'll be the company admin and can invite the rest of your team afterwards."}
        </p>

        {step === 1 ? (
          <form onSubmit={next} className="space-y-5" data-testid="signup-step1">
            <Row label="Company name" required>
              <input data-testid="company-name" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} className="input-base" required placeholder="Acme Corp" />
              {form.company_name && (
                <div className="text-xs text-slate-500 mt-2">Your workspace URL: <span className="mono text-slate-800">{slug}.peopleiq.org</span></div>
              )}
            </Row>
            <Row label="Company website">
              <input data-testid="company-website" value={form.company_website} onChange={(e) => setForm({...form, company_website: e.target.value})} className="input-base" placeholder="acme.com" />
            </Row>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Row label="Industry">
                <select data-testid="company-industry" value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})} className="input-base">
                  {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                </select>
              </Row>
              <Row label="Employees">
                <select data-testid="company-size" value={form.employee_count} onChange={(e) => setForm({...form, employee_count: e.target.value})} className="input-base">
                  {SIZES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Row>
            </div>
            <Row label="Country">
              <input data-testid="company-country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} className="input-base" />
            </Row>
            <button type="submit" data-testid="signup-next" className="btn-primary w-full justify-center">Continue <ArrowRight size={14} /></button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="signup-step2">
            <Row label="Full name" required>
              <input data-testid="admin-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" required placeholder="Sarah Chen" />
            </Row>
            <Row label="Work email" required>
              <input data-testid="admin-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input-base" required placeholder="sarah@acme.com" />
            </Row>
            <Row label="Password" required>
              <input data-testid="admin-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="input-base" required minLength={8} placeholder="Min 8 characters" />
            </Row>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-ghost flex-1 justify-center" data-testid="signup-back">Back</button>
              <button type="submit" disabled={loading} data-testid="signup-submit" className="btn-primary flex-1 justify-center">
                {loading ? "Creating workspace…" : <>Create workspace <ArrowRight size={14} /></>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">{label}{required && <span className="text-[var(--danger)] ml-1">*</span>}</label>
      {children}
    </div>
  );
}
